import React from "react";
import LegalPage from "./LegalPage";

export default function AiTransparency() {
  return (
    <LegalPage
      seoTitle="AI Transparency Policy"
      seoDescription="HelloSir.ai AI Transparency Policy — how our AI agents work, model providers, hallucination safeguards, human-in-the-loop, and end-user disclosure. A Cloud Lab Technologies (UK) product."
      seoKeywords={["AI transparency policy", "responsible AI", "AI chatbot disclosure", "AI ethics", "AI safety", "AI voice agent disclosure"]}
      path="/ai-transparency"
      tagline="// responsible AI"
      heading="AI Transparency Policy"
    >
      <p>
        Trust matters. This page explains, in plain English, how HelloSir.ai builds and operates its AI agents so that you, your customers and regulators
        always know what is happening behind every conversation. HelloSir.ai is a product of{" "}
        <a href="https://www.cloudlabweb.com" target="_blank" rel="noopener noreferrer"><strong>Cloud Lab Technologies (UK)</strong></a>.
      </p>

      <h2>1. What an Agent actually is</h2>
      <p>
        A HelloSir Agent is a configured pipeline of language and speech models. When a customer interacts with your Agent, we:
      </p>
      <ol>
        <li>Capture the text or audio input.</li>
        <li>(For voice) transcribe it with a speech-to-text model.</li>
        <li>Retrieve the most relevant passages from your knowledge base.</li>
        <li>Send the question + retrieved passages + your system prompt to a Large Language Model.</li>
        <li>Stream the reply back as text and, for voice channels, synthesise it to speech.</li>
      </ol>

      <h2>2. Models we currently use</h2>
      <p>
        We rely on best-in-class third-party model providers under enterprise zero-retention agreements. We refresh this list as the science evolves and
        we publish material changes here.
      </p>
      <ul>
        <li>Large Language Models — for reasoning and reply generation.</li>
        <li>Speech-to-Text — for transcribing inbound voice.</li>
        <li>Text-to-Speech — for synthesised agent voices.</li>
        <li>Embeddings — for semantic search of your knowledge base.</li>
      </ul>

      <h2>3. Disclosure to end-users</h2>
      <p>
        Your end-users have a right to know they are speaking to an AI. Every HelloSir widget and outbound calling agent contains a clear disclosure
        ("AI assistant", "automated agent") that you cannot disable. You can customise the wording but not remove the disclosure.
      </p>

      <h2>4. Limiting hallucinations</h2>
      <ul>
        <li>Agents are <strong>grounded</strong> in your knowledge base via retrieval-augmented generation (RAG).</li>
        <li>System prompts instruct the model to say <em>"I don't know"</em> rather than guess.</li>
        <li>You can set per-agent <strong>language locking</strong> so the Agent never switches off-topic or off-language.</li>
        <li>Confidence-scored answers can route to a human if you connect a fallback inbox or Slack channel.</li>
      </ul>

      <h2>5. Human-in-the-loop</h2>
      <p>
        Every conversation log can be reviewed in your dashboard. You can hand off to a human at any point, mark replies as good/bad, and feed corrections
        back into the knowledge base. Agents do not learn silently from individual conversations — improvements happen explicitly when you re-train.
      </p>

      <h2>6. Bias, safety & content moderation</h2>
      <p>
        We apply provider-level safety filters that block sexual content involving minors, instructions for violence, self-harm or weapons, and other
        clearly harmful content. We do not provide medical, legal or financial advice — Agents are instructed to refer users to qualified professionals.
      </p>

      <h2>7. Data your Agent does not see</h2>
      <p>
        Personal payment details, social-security numbers and credentials are filtered from logs by default. If your end-user shares such information in
        the conversation, our masking layer redacts it before it is stored.
      </p>

      <h2>8. Your obligations</h2>
      <ul>
        <li>Only upload knowledge you have the right to use.</li>
        <li>Tell your users that the Agent is automated and explain how to reach a human.</li>
        <li>Don't use Agents to deceive, harass or impersonate real people without consent.</li>
        <li>Follow local AI / consumer-protection / consent laws (TCPA, GDPR, PECR, etc.) in your jurisdiction.</li>
      </ul>

      <h2>9. Reporting an issue</h2>
      <p>
        Found a problematic reply? Email <a href="mailto:safety@hellosir.ai">safety@hellosir.ai</a> with the conversation ID and we will investigate within 5 business days.
      </p>
    </LegalPage>
  );
}
